import React from "react";
import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  Badge,
  Progress,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { IoMdTrendingUp, IoMdTime, IoMdCheckmarkCircle } from "react-icons/io";
import {
  FaBrain,
  FaChartLine,
  FaUserMd,
  FaShieldAlt,
  FaRobot,
  FaDatabase,
} from "react-icons/fa";

import { handleCTA } from "../../../../utils";

// Motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);

// Animations
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

interface WorkflowFeature {
  title: string;
  description: string;
  icon: React.ComponentType;
  color: string;
  bgColor: string;
  metrics?: {
    value: string;
    label: string;
    color: string;
  };
}

interface ROIFeature {
  title: string;
  description: string;
  icon: React.ComponentType;
  bgColor: string;
  color: string;
  percentage?: number;
}

interface SecurityFeature {
  title: string;
  description: string;
  icon: React.ComponentType;
  bgColor: string;
  color: string;
  badge?: string;
}

interface WorkflowSectionProps {
  sectionId?: string;
  logoText?: string;
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  workflowImage?: string;
  workflowFeatures?: WorkflowFeature[];
  roiFeatures?: ROIFeature[];
  securityFeatures?: SecurityFeature[];
  buttonText?: string;
  buttonAction?: () => void;
  bg?: string;
  py?: number;
  patternBg?: string;
}

function WorkflowSection({
  sectionId = "workflow",
  title = "Your clinicians want to care for patients.",
  titleHighlight = "Not type reports.",
  subtitle = "Enterprise-grade AI assistant for healthcare professionals that's hospital system approved",
  workflowFeatures = [],
  roiFeatures = [],
  securityFeatures = [],
  buttonText = "Request a Demo",
  buttonAction,
  bg,
  py = 0,
}: WorkflowSectionProps) {
  // Medical-first color palette
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const backgroundColor = bg || sectionBg;
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textPrimary = useColorModeValue("gray.900", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const purplePrimary = "#6366F1";
  const greenSuccess = "#10B981";
  const amberWarning = "#F59E0B";
  const blueInfo = "#3B82F6";

  // Default workflow features
  const defaultWorkflowFeatures: WorkflowFeature[] = [
    {
      title: "Ambient Clinical Documentation",
      description:
        "Transform patient-clinician conversations into comprehensive clinical notes automatically with 99.8% accuracy.",
      icon: FaRobot,
      color: greenSuccess,
      bgColor: `${greenSuccess}15`,
      metrics: {
        value: "99.8%",
        label: "Accuracy",
        color: greenSuccess,
      },
    },
    {
      title: "Deep EHR Integration",
      description:
        "Seamlessly integrates with Epic, Cerner, Athena, and 50+ EHR systems with bidirectional data flow.",
      icon: FaDatabase,
      color: blueInfo,
      bgColor: `${blueInfo}15`,
      metrics: {
        value: "50+",
        label: "EHR Systems",
        color: blueInfo,
      },
    },
    {
      title: "Intelligent Medical Coding",
      description:
        "Automatically suggests ICD-10, CPT, and HCC codes to improve accuracy and streamline billing.",
      icon: FaChartLine,
      color: purplePrimary,
      bgColor: `${purplePrimary}15`,
      metrics: {
        value: "20%",
        label: "More Accurate",
        color: purplePrimary,
      },
    },
  ];

  // Default ROI features
  const defaultROIFeatures: ROIFeature[] = [
    {
      title: "50% reduction in documentation time",
      description: "Clinicians save hours each day and can see more patients",
      icon: IoMdTime,
      bgColor: `${greenSuccess}15`,
      color: greenSuccess,
      percentage: 50,
    },
    {
      title: "20% increase in coding accuracy",
      description:
        "Capture appropriate reimbursements and reduce claim denials",
      icon: IoMdTrendingUp,
      bgColor: `${blueInfo}15`,
      color: blueInfo,
      percentage: 20,
    },
    {
      title: "ROI achieved by month 2",
      description:
        "Most healthcare systems achieve positive ROI in under 60 days",
      icon: IoMdCheckmarkCircle,
      bgColor: `${amberWarning}15`,
      color: amberWarning,
      percentage: 100,
    },
  ];

  // Default security features
  const defaultSecurityFeatures: SecurityFeature[] = [
    {
      title: "HIPAA compliant architecture",
      description: "Built with privacy and security as foundational principles",
      icon: FaShieldAlt,
      bgColor: `${greenSuccess}15`,
      color: greenSuccess,
      badge: "HIPAA",
    },
    {
      title: "Minimized hallucination risk",
      description:
        "Advanced models trained specifically for healthcare accuracy",
      icon: FaBrain,
      bgColor: `${purplePrimary}15`,
      color: purplePrimary,
      badge: "AI-Safe",
    },
    {
      title: "Clinician review before EHR submission",
      description:
        "Human-in-the-loop design ensures quality and accountability",
      icon: FaUserMd,
      bgColor: `${blueInfo}15`,
      color: blueInfo,
      badge: "Human-in-Loop",
    },
  ];

  const displayWorkflowFeatures =
    workflowFeatures.length > 0 ? workflowFeatures : defaultWorkflowFeatures;
  const displayROIFeatures =
    roiFeatures.length > 0 ? roiFeatures : defaultROIFeatures;
  const displaySecurityFeatures =
    securityFeatures.length > 0 ? securityFeatures : defaultSecurityFeatures;

  const handleButtonClick = () => {
    if (buttonAction) {
      buttonAction();
    } else {
      handleCTA("signup");
    }
  };

  return (
    <Box
      as="section"
      py={py}
      bg={backgroundColor}
      position="relative"
      overflow="hidden"
      id={sectionId}
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
    >
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={8}>
          {/* Header Section */}
          <Box textAlign="center" maxW="container.lg" mx="auto">
            {/* <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Flex align="center" justify="center" mb={6}>
                <Logo size="4xl" />
                <Heading
                  size="lg"
                  color={purplePrimary}
                  fontFamily="Barlow Condensed"
                  fontWeight="bold"
                  ml={3}
                >
                  {logoText}
                </Heading>
              </Flex>
            </MotionBox> */}

            <MotionHeading
              size="2xl"
              mb={6}
              color={textPrimary}
              lineHeight="1.2"
              fontWeight="700"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              {...fadeInUp}
            >
              {title}{" "}
              <Text as="span" color={purplePrimary}>
                {titleHighlight}
              </Text>
            </MotionHeading>

            <MotionText
              fontSize={{ base: "lg", md: "xl" }}
              color={textSecondary}
              maxW="container.md"
              mx="auto"
              mb={8}
              lineHeight="1.6"
              {...fadeInUp}
              transition={{ delay: 0.2 }}
            >
              {subtitle}
            </MotionText>

            {/* Trust Indicators */}
            {/* <HStack spacing={8} justify="center">
              <VStack spacing={1}>
                <Icon as={FaShieldAlt} boxSize={6} color={greenSuccess} />
                <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                  HIPAA Compliant
                </Text>
              </VStack>
              <VStack spacing={1}>
                <Icon
                  as={IoMdCheckmarkCircle}
                  boxSize={6}
                  color={purplePrimary}
                />
                <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                  99.8% Accuracy
                </Text>
              </VStack>
              <VStack spacing={1}>
                <Icon as={FaHospital} boxSize={6} color={blueInfo} />
                <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                  500+ Hospitals
                </Text>
              </VStack>
            </HStack> */}
          </Box>

          {/* Workflow Features */}
          <MotionBox
            w="full"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} width="100%">
              {displayWorkflowFeatures.map((feature, idx) => (
                <MotionBox
                  key={idx}
                  variants={fadeInUp}
                  whileHover={{
                    y: -4,
                    transition: { duration: 0.3 },
                  }}
                >
                  <Box
                    bg={cardBg}
                    p={6}
                    borderRadius="xl"
                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                    position="relative"
                    border={`1px solid ${borderColor}`}
                    overflow="hidden"
                    transition="all 0.3s ease"
                    _hover={{
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                      borderColor: feature.color,
                    }}
                  >
                    {/* Feature Icon */}
                    <Box
                      mb={4}
                      bg={feature.bgColor}
                      color={feature.color}
                      borderRadius="lg"
                      p={3}
                      width="fit-content"
                    >
                      <Icon as={feature.icon} boxSize={6} />
                    </Box>

                    {/* Metrics Badge */}
                    {feature.metrics && (
                      <Box position="absolute" top={4} right={4}>
                        <Badge
                          px={2}
                          py={1}
                          bg={`${feature.metrics.color}15`}
                          color={feature.metrics.color}
                          fontSize="xs"
                          fontWeight="600"
                          borderRadius="full"
                        >
                          {feature.metrics.value} {feature.metrics.label}
                        </Badge>
                      </Box>
                    )}

                    <VStack spacing={3} align="flex-start">
                      <Heading size="md" color={textPrimary} fontWeight="700">
                        {feature.title}
                      </Heading>
                      <Text
                        color={textSecondary}
                        fontSize="sm"
                        lineHeight="1.6"
                      >
                        {feature.description}
                      </Text>
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionBox>

          {/* Workflow Image */}
          {/* {workflowImage && (
            <MotionBox
              maxW="1000px"
              mx="auto"
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
            >
              <Box
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
                borderRadius="2xl"
                overflow="hidden"
                bg={cardBg}
                p={6}
                border={`1px solid ${borderColor}`}
              >
                <MotionImage
                  src={workflowImage}
                  alt="Healthcare AI Workflow"
                  width="100%"
                  height="auto"
                  whileHover={{ scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                />
              </Box>
            </MotionBox>
          )} */}

          {/* ROI and Security Features */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} width="100%">
            {/* ROI Features */}
            <MotionBox
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
            >
              <VStack spacing={6} align="flex-start">
                <Heading size="lg" color={textPrimary} fontWeight="700">
                  Proven ROI, Fast Results
                </Heading>
                <VStack spacing={4} align="flex-start" w="full">
                  {displayROIFeatures.map((feature, idx) => (
                    <Box key={idx} w="full">
                      <HStack align="start" spacing={3} mb={2}>
                        <Box
                          borderRadius="lg"
                          bg={feature.bgColor}
                          p={2}
                          color={feature.color}
                          boxSize={10}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Icon as={feature.icon} boxSize={5} />
                        </Box>
                        <VStack align="flex-start" spacing={1} flex="1">
                          <Text
                            fontWeight="700"
                            fontSize="md"
                            color={textPrimary}
                          >
                            {feature.title}
                          </Text>
                          <Text color={textSecondary} fontSize="sm">
                            {feature.description}
                          </Text>
                        </VStack>
                      </HStack>
                      {feature.percentage && (
                        <Box mt={2}>
                          <HStack justify="space-between" mb={1}>
                            <Text
                              fontSize="xs"
                              fontWeight="600"
                              color={textSecondary}
                            >
                              Improvement
                            </Text>
                            <Text
                              fontSize="xs"
                              fontWeight="700"
                              color={feature.color}
                            >
                              {feature.percentage}%
                            </Text>
                          </HStack>
                          <Progress
                            value={feature.percentage}
                            colorScheme="purple"
                            bg={`${feature.color}20`}
                            borderRadius="full"
                            height="6px"
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </VStack>
              </VStack>
            </MotionBox>

            {/* Security Features */}
            <MotionBox
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
            >
              <VStack spacing={6} align="flex-start">
                <Heading size="lg" color={textPrimary} fontWeight="700">
                  AI That's Safe and Secure
                </Heading>
                <VStack spacing={4} align="flex-start" w="full">
                  {displaySecurityFeatures.map((feature, idx) => (
                    <Box key={idx} w="full">
                      <HStack align="start" spacing={3} mb={2}>
                        <Box
                          borderRadius="lg"
                          bg={feature.bgColor}
                          p={2}
                          color={feature.color}
                          boxSize={10}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Icon as={feature.icon} boxSize={5} />
                        </Box>
                        <VStack align="flex-start" spacing={1} flex="1">
                          <HStack spacing={2}>
                            <Text
                              fontWeight="700"
                              fontSize="md"
                              color={textPrimary}
                            >
                              {feature.title}
                            </Text>
                            {feature.badge && (
                              <Badge
                                px={2}
                                py={1}
                                bg={`${feature.color}15`}
                                color={feature.color}
                                fontSize="xs"
                                fontWeight="600"
                                borderRadius="full"
                              >
                                {feature.badge}
                              </Badge>
                            )}
                          </HStack>
                          <Text color={textSecondary} fontSize="sm">
                            {feature.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </VStack>
            </MotionBox>
          </SimpleGrid>

          {/* CTA Section */}
          <MotionBox
            textAlign="center"
            {...fadeInUp}
            transition={{ delay: 0.4 }}
          >
            <MotionButton
              size="lg"
              bg={purplePrimary}
              color="white"
              _hover={{
                bg: `${purplePrimary}dd`,
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
              }}
              _active={{
                transform: "translateY(0px)",
              }}
              borderRadius="xl"
              px={10}
              py={5}
              fontWeight="700"
              fontSize="lg"
              onClick={handleButtonClick}
              transition="all 0.3s ease"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {buttonText}
            </MotionButton>
          </MotionBox>
        </VStack>
      </Container>
    </Box>
  );
}

export default WorkflowSection; 